/**
 * How an event is named in titles, H1s and schema.
 *
 * The catalog's `name` already carries the performer for most music and
 * comedy rows ("Rod Wave with FRIDAYY", "Harry Styles with Jamie XX"), so
 * prefixing the artist again produced "Rod Wave — Rod Wave with FRIDAYY" in
 * every <title>, H1 and Event schema name on the site. ChatGPT's instant mode
 * reads exactly the title and the ~200 characters after the H1, and Bing
 * matches titles to the buyer's words — a doubled name wastes both.
 *
 * Rule: prefix the artist only when the name does not already start with it.
 * scripts/gen-catalog.mjs applies the same rule when it writes the edge
 * metadata row (functions/events-meta.json); keep the two in sync.
 */
import { displayName } from '../../shared/slug.mjs';
import { cardName } from '../../shared/event-title.mjs';

/** A card's or row's title and small line: the matchup first, the competition under it (shared/event-title.mjs). */
export const eventCardName = (e: { name: string; artist?: string | null; category?: string | null; teams?: string[] | null; teamNames?: string[] | null }): { title: string; sub: string | null } =>
  cardName(e);

export const eventDisplayName = (artist: string | undefined, name: string): string => displayName(artist, name);

/** The part of the name that is not the artist ("with FRIDAYY"); empty when
 *  the name is just the artist, the full name when it does not start with it. */
export const eventSubtitle = (artist: string | undefined, name: string): string => {
  const n = name.trim();
  if (!artist) return n;
  const a = artist.trim();
  if (!a || !n.toLowerCase().startsWith(a.toLowerCase())) return n;
  return n.slice(a.length).replace(/^[\s:,·—–-]+/, '');
};

/** Search rows: a game reads "A vs. B" with its competition after it; everything else keeps eventDisplayName. */
export const eventRowName = (e: { name: string; artist?: string | null; category?: string | null; teams?: string[] | null; teamNames?: string[] | null }): { title: string; sub: string | null } => {
  const c = cardName(e);
  return c.sub ? c : { title: displayName(e.artist ?? undefined, e.name), sub: null };
};
